import { css } from 'styled-components';

import { VALID_PLACEMENTS } from '../Header/Dropdown/Drawer/StyledHeaderDrawer';
import { themeMerge } from '../Utils';

import IThemeInterface, { breakPointSizes, edgeSize, zIndices } from './common';

const buttonDisabledOverride: Partial<IThemeInterface['button']['base']> = {
  background: 'gray300',
  font: { color: 'disabledText' },
  border: { color: 'gray300' }
};

const buttonFocusOverride: Partial<IThemeInterface['button']['base']> = {
  outline: { size: 'double', style: 'auto', offset: '3px' }
};

const theme: IThemeInterface = themeMerge({
  name: 'capitalOne',

  global: {
    breakPointSizes,

    colors: {
      blue60: '#013D5B',
      blue40: '#255f82',
      blue30: '#1653C0',
      blue10: '#c0e7f3',
      gray700: '#3d3d3d',
      gray600: '#525252',
      gray500: '#696969',
      gray400: '#7d7d7d',
      gray300: '#d4d4d4',
      gray100: '#f4f4f4',
      gray75: '#bfbfbf',
      gray50: '#f9f9f9',
      green70: '#008140',
      green60: '#306e3e',
      green10: '#dae8d8',
      red60: '#ba2629',
      red50: '#cc2427',
      red10: '#fcd5d1',
      yellow50: '#f9c606',
      yellow10: '#fff1d0',

      accentBlue: '#0178A2',
      black: '#141414',
      disabled: 'gray100',
      disabledText: 'gray400',
      error: 'red50',
      focus: 'blue30',
      info: 'blue30',
      link: 'blue40',
      primary: 'blue40',
      secondary: 'green70',
      success: 'green70',
      text: 'black',
      warning: 'yellow50',
      white: '#fff',
    },

    edgeSize,

    elevation: {
      '1x': { color: 'rgba(0,0,0,0.2)', shadow: '0 1px 4px 0' },
      '2x': { color: 'rgba(0,0,0,0.2)', shadow: '0 2px 8px 0' },
      '3x': { color: 'rgba(0,0,0,0.2)', shadow: '0 4px 16px 0' },
    },

    font: {
      family: {
        optimist: 'optimist',
        optimist_light: 'optimist_light',
        optimist_xlight: 'optimist_xlight',
        optimist_semibold: 'optimist_semibold',
        optimist_bold: 'optimist_bold',
      },

      size: {
        heroLarge: '3.2rem',
        heroMedium: '2.4rem',
        heroSmall: '2rem',
        body: '1.6rem',
        bodyLarge: '1.8rem',
        bodyMedium: '1.4rem',
        bodySmall: '1.2rem',
      },
    },

    outline: {
      color: 'focus',
      size: 'single',
      style: 'solid',
    },

    zIndex: zIndices,
  },

  banner: {
    info: {
      backgroundColor: 'blue10',
      border: {
        side: 'left',
        color: 'primary',
        size: 'quadruple',
      },
      padding: { vertical: '1.5x', horizontal: '2x' },
      inlineLink: {
        color: 'text',
        hoverColor: 'text',
      },
      closeIcon: {
        color: 'text'
      },
    },
    warning: {
      backgroundColor: 'yellow10',
      border: { color: 'yellow50' },
      inlineLink: {
        color: 'text',
        hoverColor: 'text',
      },
      closeIcon: {
        color: 'text'
      },
    },
    success: {
      backgroundColor: 'green10',
      border: { color: 'green70' },
      inlineLink: {
        color: 'text',
        hoverColor: 'text',
      },
      closeIcon: {
        color: 'text'
      },
    },
    error: {
      backgroundColor: 'red10',
      border: { color: 'red50' },
      inlineLink: {
        color: 'text',
        hoverColor: 'text',
      },
      closeIcon: {
        color: 'text'
      },
    },
  },

  button: {
    base: {
      background: 'primary',
      border: 'none',
      borderRadius: '6px',
      shadow: 'none',
      font: {
        color: 'white',
        size: 'heroSmall',
        letterSpacing: '',
        lineHeight: '1.5',
        textTransform: '',
        weight: ''
      },
      outline: 'none',
      padding: { vertical: '0.9rem', horizontal: '2x' },
      width: { base: '100%', min: '11em'},
      additionalStyles: '',
      loadingIconInsideButton: true,
      spinner: {
        color: {
          circleOutline: 'white',
        },
      },
    },
    overrides: {
      primary: {
        state: {
          disabled: buttonDisabledOverride,
          hover: { background: 'blue60' },
          focus: buttonFocusOverride,
          isLoading: { background: 'blue60' }, // temporary until cof blue50/blue60 color updates. will be #10253F once complete.
        },
      },

      secondary: {
        base: {
          background: 'secondary',
          font: { color: 'white' },
        },
        state: {
          disabled: buttonDisabledOverride,
          hover: { background: 'green60' },
          focus: buttonFocusOverride
        }
      },

      regressive: {
        base: {
          background: 'gray400',
          font: { color: 'white' },
        },
        state: {
          disabled: buttonDisabledOverride,
          hover: { background: 'gray500' },
          focus: buttonFocusOverride
        }
      },

      destructive: {
        base: {
          background: 'error',
          font: { color: 'white' },
        },
        state: {
          disabled: buttonDisabledOverride,
          hover: { background: 'red60' },
          focus: buttonFocusOverride
        }
      },

      link: {
        base: {
          background: 'none',
          font: {
            color: 'link',
            textDecoration: 'underline',
            size: 'body',
            lineHeight: '1.5',
          },
          border: { size: 'none' },
          padding: 'none',
          width: { base: 'auto', min: 'auto'},
        },
        state: {
          hover: { font: { color: 'blue60' } },
          focus: {
            font: { color: 'blue60' },
            outline: { color: 'primary' },
          },
          disabled: { font: { color: 'disabledText' } }
        }
      },

      text: {
        base: {
          background: 'none',
          font: { color: 'primary', size: 'body' },
          border: { size: 'double', color: 'transparent' },
          padding: 'quadruple',
          width: { base: 'auto', min: 'auto' },
        },
        state: {
          disabled: {
            font: { color: 'disabledText' }
          },
          focus: {
            outline: { size: 'double', style: 'auto' },
          }
        }
      }
    }
  },

  circleProgress: {
    backgroundPathColor: 'gray300',
    percentagePathColor: 'success',
    label: {
      percentage: {
        number: {
          size: 'heroLarge',
          lineHeight: '1.25',
          color: 'black',
          family: 'optimist_light',
        },
        sign: {
          size: 'body',
          weight: '600',
          verticalAlign: 'super',
        }
      },
      content: {
        size: 'bodyMedium',
        color: 'black',
      },
    },
  },

  containerSize: {
    full: '100%',
    narrow: '51.2rem', // 512px
    'narrow-2': '70.4rem', // 704px
    wide: '89.6rem', // 896px
    max: '128rem', // 1280px
  },

  datePicker: {
    input: {
      // tslint:disable:max-line-length
      icon: (color: string) => `"data:image/svg+xml,%3Csvg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M19.0002 7.99999C18.8722 7.99999 18.7442 8.04899 18.6462 8.14599L12.0002 14.793L5.35425 8.14599C5.15825 7.95099 4.84225 7.95099 4.64625 8.14599C4.45125 8.34199 4.45125 8.65799 4.64625 8.85399L11.6462 15.854C11.8422 16.049 12.1582 16.049 12.3542 15.854L19.3542 8.85399C19.5492 8.65799 19.5492 8.34199 19.3542 8.14599C19.2563 8.04899 19.1283 7.99999 19.0002 7.99999Z' fill='${encodeURIComponent(color)}'/%3E%3C/svg%3E%0A"`,
      iconColor: {
        base: 'black',
        disabled: 'gray400'
      },
      valueFormat: 'MM/DD/YYYY'
    },
    navButton: {
      color: 'primary',
      nextIcon: (color: string) => `"data:image/svg+xml;charset=utf8,%3Csvg viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M20.9473 12.1914C20.9229 12.2504 20.8867 12.3056 20.8388 12.3536L13.0606 20.1317C12.8653 20.327 12.5487 20.327 12.3535 20.1317C12.1582 19.9365 12.1582 19.6199 12.3535 19.4246L19.2781 12.5L3.51465 12.5C3.23851 12.5 3.01465 12.2761 3.01465 12C3.01465 11.7239 3.23851 11.5 3.51465 11.5L19.2781 11.5L12.3535 4.57539C12.1582 4.38012 12.1582 4.06354 12.3535 3.86828C12.5487 3.67302 12.8653 3.67302 13.0606 3.86828L20.8388 11.6465C20.8867 11.6944 20.9229 11.7496 20.9473 11.8086C20.9695 11.8622 20.9819 11.9189 20.9846 11.976C20.985 11.984 20.9852 11.992 20.9852 12C20.9852 12.0678 20.9717 12.1324 20.9473 12.1914Z' fill='${encodeURIComponent(color)}' fillRule='nonzero' /%3E%3C/svg%3E"`,

      prevIcon: (color: string) => `"data:image/svg+xml;charset=utf8,%3Csvg viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M3.05273 12.1914C3.07713 12.2504 3.1133 12.3056 3.16124 12.3536L10.9394 20.1317C11.1347 20.327 11.4513 20.327 11.6465 20.1317C11.8418 19.9365 11.8418 19.6199 11.6465 19.4246L4.7219 12.5L20.4854 12.5C20.7615 12.5 20.9854 12.2761 20.9854 12C20.9854 11.7239 20.7615 11.5 20.4854 11.5L4.7219 11.5L11.6465 4.57539C11.8418 4.38012 11.8418 4.06354 11.6465 3.86828C11.4513 3.67302 11.1347 3.67302 10.9394 3.86828L3.16124 11.6465C3.1133 11.6944 3.07713 11.7496 3.05273 11.8086C3.03054 11.8622 3.01809 11.9189 3.01536 11.976C3.01498 11.984 3.01479 11.992 3.01479 12C3.01479 12.0678 3.02828 12.1324 3.05273 12.1914Z' fill='${encodeURIComponent(color)}' fillRule='nonzero' /%3E%3C/svg%3E"`,
      // tslint:enable:max-line-length
      backgroundSize: '100%'
    },
    monthLabel: {
      fontSize: 'body'
    },
    weekdayLabel: {
      color: 'black',
      fontSize: 'bodyMedium',
      fontWeight: '700',
      border: { side: 'bottom', color: 'gray300' }
    },
    dayCell: {
      borderRadius: { topLeft: '0px', bottomLeft: '0px', topRight: '0px', bottomRight: '0px' },
      selected: {
        color: 'white',
        backgroundColor: 'primary',
      },
      hover: {
        backgroundColor: 'primary',
        color: 'white'
      },
      disabled: {
        color: 'disabledText'
      },
      due: {
        fontWeight: '700',
        dueText: {
          fontSize: 'bodySmall',
          color: 'primary',
          lineHeight: '1.3',
          hover: {
            color: 'white',
          }
        }
      },
      fontSize: 'bodyMedium',
      padding: {top: '.5em', right: '.7em', left: '.7em', bottom: '1.5em'},
      border: { side: 'bottom', color: 'gray300' },
      leadingZeros: true
    },
    overlay: {
      borderRadius: '0',
      backgroundColor: 'white',
      boxShadow: '1x',
      width: '29rem',
    },
    dayPickerProps: {
      months: ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'],
      weekdaysShort: ['S', 'M', 'T', 'W', 'T', 'F', 'S']
    },
    focusRing: {
      outline: { size: 'double', style: 'auto', offset: 'triple' },
    },
  },

  descriptionList: {
    term: {
      family: 'optimist',
      weight: '600',
      color: 'black',
      size: 'body',
      margin: { right: '3x' },
      lineHeight: '1.5',
    },

    detail: {
      family: 'optimist_light',
      weight: '400',
      color: 'black',
      size: 'body',
      lineHeight: '1.5',
    },

    variant: {
      large: {
        term: {
          lineHeight: '1.45',
          size: 'bodyMedium',
        },
        detail: {
          family: 'optimist_light',
          size: 'heroLarge',
          lineHeight: '1.25',
          weight: '300',
        },
      },

      small: {
        term: {
          weight: '700',
          lineHeight: '1.45',
          size: 'bodyMedium',
        },
        detail: {
          weight: '300',
          family: 'optimist_light',
          size: 'heroSmall',
          lineHeight: '1.5'
        },
      },
    },
  },

  form: {
    checkbox: {
      backgroundColor: 'white',
      border: { color: 'gray500' },
      icon: {
        size: '3x',
        color: 'white',
      },
      label: {
        font: { color: 'text', size: 'body', lineHeight: '1.6' },
      },
      round: 'double',
      size: '3x',
      margin: { right: '2x' },

      state: {
        checked: {
          backgroundColor: 'primary',
          border: { color: 'primary' },
        },

        checkedHover: {
          border: { color: 'primary' },
        },

        disabled: {
          backgroundColor: 'disabled',
          border: { color: 'gray300' },
          label: { font: { color: 'gray500' }},
        },

        disabledChecked: {
          backgroundColor: 'gray300',
        },

        error: {},

        focus: {
          focusPosition: 'indicatorOnly',
          outline: { size: 'double', style: 'auto', offset: 'triple' },
        },

        hover: {
          border: { color: 'black' }
        },
      },
    },

    layout: {
      wrapper: {
        row: { margin: { bottom: '3x' } },
        stackRow: {
          margin: {
            base: { right: '0' },
            small: { right: '2x' },
          },
        },
      },
    },

    errorText: {
      checkboxAndRadioErrorIndention: true,
      font: {
        color: 'error',
        size: 'bodySmall',
      },
      margin: { top: '1x' },
    },

    helperText: {
      font: {
        color: { default: 'gray700', hover: 'black' },
        size: 'bodySmall',
        weight: '500',
      },
      margin: { top: '1x' },
    },

    input: {
      backgroundColor: 'white',
      border: { color: 'gray400', size: 'single', side: 'all' },
      iconColor: 'black',
      padding: { horizontal: '1.5x', top: '1.1rem', bottom: '1.1rem' },
      placeholder: 'gray700',
      round: 'quadruple',
      font: { lineHeight: '1.5' },

      state: {
        hover: {
          border: { color: 'black' },
        },

        focus: {
          border: { color: 'primary', size: 'single', side: 'all' },
          shadow: { color: 'primary', inset: true, shadow: '0 0 0 1px' },
        },

        error: {
          border: { color: 'error', },
          shadow: { color: 'error', inset: true, shadow: '0 0 0 1px' },
        },

        disabled: {
          border: { color: 'gray75', size: 'single', side: 'all' },
          backgroundColor: 'disabled',
          font: { color: 'gray75' },
        },
      },
    },

    label: {
      font: {
        color: {
          default: 'gray700',
          hover: 'black',
          disabled: 'gray75',
        },
        size: 'bodyMedium',
        weight: '500',
      },
      margin: { bottom: '1x' },

      // COF's font was causing some quirks in Safari browsers only. This fixes those issue and is applying them only to Safari
      additionalStyles: css`
        @media not all and (min-resolution:.001dpcm) {
          white-space: nowrap;
        }
      `
    },

    legend: {
      font: {
        size: 'bodyMedium',
        color: 'black',
      },
      margin: { bottom: '1.5x' },
    },

    radio: {
      button: {
        backgroundColor: 'white',
        checkedStyle: 'ring',
        border: { color: 'black' },
        label: {
          font: { lineHeight: '1.5' },
          margin: { left: '5x' },
        },
        size: '3x',

        state: {
          checked: {
            shadow: { color: 'primary', inset: true, shadow: '0 0 0 7px' },
          },

          checkedHover: {
            border: 'none',
            shadow: { color: 'blue60', inset: true, shadow: '0 0 0 7px' },
          },

          disabled: {
            border: { color: 'gray300' },
            backgroundColor: 'gray100',
            label: { font: { color: 'gray500'} },
          },

          disabledChecked: {
            backgroundColor: 'white',
            border: 'none',
            shadow: { color: 'gray300', inset: true, shadow: '0 0 0 7px' },
          },

          error: {
          },

          focus: {
            focusPosition: 'indicatorOnly',
          },

          hover: {
            border: { size: 'double', color: 'black' },
          },
        },
      },

      card: {
        backgroundColor: 'white',
        border: { color: 'white', size: 'single'},
        round: 'quadruple',
        shadow: '1x',

        state: {
          checked: { border: { size: 'double', color: 'blue60' }},
          hover: { border: { size: 'single', color: 'blue60' }},
          focus: {
            border: { size: 'double', color: 'transparent' },
            outline: { offset: '5px', style: 'auto' },
          },

          error: {
            text: {
              margin: { top: '3x' }
            }
          },
        },
      },

      badge: {
        backgroundColor: 'white',
        border: { color: 'gray500' },
        padding: '1.5x',
        round: 'quadruple',
        width: { min: '4.8125em' },
        shadow: '1x',
        font: { color: 'text' },

        state: {
          checked: {
            shadow: { shadow: '0 0 0 1px', color: 'primary', inset: true },
            border: { size: 'single', color: 'primary' },
          },

          checkedHover: {
            shadow: { shadow: '0 0 0 1px', color: 'primary', inset: true },
            border: { size: 'single', color: 'primary' },
          },

          focus: {
            outline: { size: 'double', style: 'auto', offset: 'triple' },
          },

          hover: {
            border: { color: 'black' },
          },

          error: {
            border: { color: 'error' },
            shadow: 'none',
          },

          disabled: {
            backgroundColor: 'gray100',
            border: { color: 'gray300' },
            font: { color: 'disabledText' },
          },

          disabledChecked: {
            backgroundColor: 'gray300',
            border: { color: 'gray300' },
            font: { color: 'white' },
          },
        },
      },

      toggle: {
        focusPosition: 'indicatorOnly',

        label: {
          color: 'black',
          size: 'body',
        },

        button: {
          margin: { right: '1x' },
          padding: { vertical: '0.75x', horizontal: '0.75x' },
          border: { color: 'gray500' },
          backgroundColor: 'white',
        },

        slidingIndicator: {
          size: '1x',
          color: 'gray400',
        },

        state: {
          checked: {
            label: { color: 'black' },
            button: {
              border: { color: 'primary' },
              backgroundColor: 'primary',
            },
            slidingIndicator: {
              color: 'white',
            },
          },

          disabled: {
            label: { color: 'gray400' },
            button: {
              border: { color: 'gray300' },
              backgroundColor: 'gray100',
            },
            slidingIndicator: {
              color: 'gray300',
            },
          },

          disabledChecked: {
            label: { color: 'gray400' },
            button: {
              border: { color: 'gray300' },
              backgroundColor: 'gray300',
            },
            slidingIndicator: {
              color: 'white',
            },
          },

          focus: {
            button: {
              shadow: [
                { color: 'white', shadow: '0 0 0 2px' },
                { color: 'focus', shadow: '0 0 0 4px' },
              ],
            },
          },

          hover: {
            label: { color: 'black' },
            button: {
              border: { color: 'black' },
              backgroundColor: '',
            },
            slidingIndicator: {
              color: 'black',
            },
          },
        },
      },
    },

    slider: {
      outline: { color: 'focus' },
      thumb: {
        color: { default: 'blue60', disabled: 'gray500' },
        height: 36,
        width: 36,
      },
      track: {
        color: {
          base: { default: 'gray100', disabled: 'gray50' },
          progress: { default: 'blue40', disabled: 'gray300' }
        },
        thickness: 8,
        borderRadius: '4px',
      },
      unitLabels: {
        color: { default: 'black', disabled: 'gray700' },
        margin: { top: '2x' },
      }
    },

    textarea: {
      backgroundColor: 'white',
      border: { color: 'gray700' },
      font: { color: 'gray700' },
      padding: { horizontal: '2x', top: '2x', bottom: '2x' },
      round: '4px',

      state: {
        hover: {
          border: { color: 'black' },
        },

        focus: {
          shadow: { color: 'primary', shadow: '0 0 0 1px', inset: true, },
          border: { color: 'primary' }
        },

        error: {
          border: { color: 'error' },
        },

        disabled: {
          backgroundColor: 'gray100',
          border: { color: 'gray75' },
          font: { color: 'gray75' },
        },
      },
    },
  },

  heading: {
    default: {
      color: 'black',
      family: 'optimist_light',
      lineHeight: '1.5',
    },

    level: {
      h1: {
        size: { base: 'heroMedium', medium: 'heroLarge' },
        lineHeight: '1.25',
      },

      h2: {
        size: { base: 'heroSmall', medium: 'heroMedium' },
      },

      h3: {
        family: 'optimist',
        size: { base: 'bodyLarge', medium: 'heroSmall' },
      },

      h4: {/* level specific styles */ },
      h5: {/* level specific styles */ },
      h6: {/* level specific styles */ },
    },
  },

  layout: {
    header: {
      border: { size: '1px', side: 'bottom', color: 'gray300' },
      padding: {
        base: { horizontal: '3x' },
        large: { horizontal: '10x' },
        xlarge: { horizontal: '5%' },
      },
      height: { base: '6.4rem', small: '8rem' },

      dropdown: {
        shadow: '1x',
        width: { base: '100%', small: '24rem' },
        backgroundColor: 'white',
        border: 'none',
        icon: {
          desktop: {
            size: '2.75x',
            color: 'black',
            type: 'arrow-bottom',
          },
          mobile: {
            open: {
              type: 'menu',
              color: 'black',
              size: '3x'
            },
            close: {
              size: '3x',
              color: 'black',
              type: 'close'
            }
          }
        },

        button: {
          font: {
            color: 'black',
            size: 'body',
            lineHeight: '1.5'
          },

          state: {
            hover: { font: { color: 'link' }},
          },
        },

        link: {
          divider: {
            placement: VALID_PLACEMENTS.allChildren,
            height: '1px',
            backgroundColor: 'gray300',
            width: { base: '100%', small: 'calc(100% - 3rem)'}
          },
          border: { color: 'transparent' },
          font: {
            color: 'black',
            size: 'body',
            lineHeight: '1.5',
            align: { base: 'center', small: 'left' },
          },

          padding: { vertical: '1.875x', horizontal: '2x' },

          state: {
            hover: { font: { color: 'link' }},
            focus: { border: { color: 'focus' }},
          },
        }
      },

      link: {
        font: {
          color: 'black',
          lineHeight: '1.5',
          size: 'body',
        },
        margin: {
          right: '4x',
        },
        state: {
          hover: { font: { color: 'link' },
          },
        },
      },
    },
  },

  link: {
    inline: {
      state: {
        hover: { color: 'blue60' },
      },
    },
  },

  list: {
    margin: { left: '2x' },

    listItem: {
      common: {
        margin: { bottom: '1x' },
        font: {
          family: 'optimist',
          lineHeight: '1.5',
          size: 'body',
          color: 'black',
          weight: '400'
        },
      },

      bullet: {
        backgroundColor: 'black',
        listStyleSpacing: '2.6rem',
        size: '0.75x',
      },

      icon: {
        listStyleSpacing: '1.5x',
        size: '2.5x',
        type: 'done',
        color: 'black',
      },

      number: {
        listStyleSpacing: '3.4rem',
        font: {
          weight: '400',
          color: 'black',
        },
      },
    },
  },

  modal: {
    overlay: {
      backgroundColor: 'rgba(28, 43, 57, 0.7)',
      padding: {
        base: { top: '3x' },
        medium: { top: '11x' },
      },
    },
    card: {
      backgroundColor: 'white',
      border: { color: 'white' },
      padding: {
        base: { vertical: '6x', horizontal: '3x' },
        medium: { horizontal: '4x' },
      },
      round: 'quadruple',
      shadow: '1x'
    },
    closeIcon: {
      type: 'alert-close',
      size: '2x',
      color: 'black',
      padding: '2.5x'
    },
  },

  spinner: {
    size: '2em',
    iconStrokeWidth: 2,
    withOutline: false,
    color: {
      circleOutline: 'primary',
    },
  },

  table: {
    backgroundColor: {
      base: 'white',
      small: 'gray100',
    },
    border: 'none',

    tableRow: {
      height: '1px',
      borderGutter: '24px',
    },

    tableHeader: {
      rowDivider: 'transparent',
      padding: { top: '2.5x', horizontal: '3x', bottom: '2x' },
      font: {
        size: 'bodyMedium',
        weight: '600',
      },
    },

    tableBody: {
      backgroundColor: 'white',
      rowDivider: 'gray300',
      boxShadow: '0 1px 4px 0 rgba(0, 0, 0, 0.2)',
      borderRadius: {
        topLeft: '4px',
        topRight: '4px',
        bottomLeft: '4px',
        bottomRight: '4px',
      },
      padding: {
        base: { vertical: 'quadruple', horizontal: '3x' },
        small: { vertical: '2.5x', horizontal: '3x' },
      },
      font: {
        size: 'body',
      },
    },
  },

  tooltip: {
    activationType: 'automatic',
    pane: {
      background: 'black',
      padding: '2x',
      shadow: '3x',
      round: 'double',
      margin: { right: '5%' },
      verticalOffset: '10%',
      position: 'bottom',

      font: {
        size: 'bodyMedium',
        color: 'white',
      },
    },

    icon: {
      size: '2rem',
      color: 'primary',
      hover: 'blue60',
      margin: { left: 'double' },
    },
  },

  typography: {
    text: {
      body: {
        default: {
          color: 'black',
          lineHeight: '1.5',
          family: 'optimist',
        },
        large: { lineHeight: '1.45' },
        medium: { lineHeight: '1.45' },
      },
    },

    pill: {
      default: {
        padding: { vertical: '1x', horizontal: '2x' },
        font: {
          transform: 'uppercase',
          color: 'white',
          family: 'optimist_semibold',
          size: 'bodySmall',
          lineHeight: '1.35',
          weight: '700',
        },
        round: '100px',
      },
      neutral: { backgroundColor: 'gray500' },
      success: { backgroundColor: 'success' },
      warning: {
        backgroundColor: 'warning',
        font: { color: 'black' }
      },
      error: { backgroundColor: 'error' },
      info: { backgroundColor: 'info' },
    },
  },
});

export default theme;
